import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { FirmwareInfo, copyFirmwareDescriptor, getNewFirmwareDescriptor } from '@/features/firmware/firmwaresSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import MenuItem from '@/components/Common/MenuItem';
import { NumberText } from '@/components/Common/Field';
import { DeleteModal, RightBar } from '@/components/Common/Modals';
import { RoleContext } from '@/app/api/common';

import Firmware from './Firmware';
import { getPlatformDisplayName } from '@/components/Common/FirmwaresEditor';
import { usePageSubscription } from '@/app/websocket/hooks';

const Firmwares = () => {
  const { t } = useTranslation();

  usePageSubscription([{ type: 'firmware' }]);

  const firmwares = useAppSelector((state) => state.firmwares.firmwares);
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as FirmwareInfo[]);
  const [deleteNum, setDeleteNum] = React.useState(-1);
  const role = React.useContext(RoleContext);
  console.log("firmwares",firmwares)
  const [firmwareNum, setFirmwareNum] = React.useState(-1);

  React.useEffect(() => {
    console.log("Firmwares component mounted - calling getFirmwares API");
    store.getFirmwares();
  }, []);

  React.useEffect(() => {
    setCols([
      {
        field:      "platform",
        headerName: t("Firmware:platform"),
        flex:       1,
        minWidth:   100,
        valueGetter: (params: any) => {
          return getPlatformDisplayName(params.data?.platform || "");
        },
      },
      {
        field:      "model",
        headerName: t("Firmware:model"),
        flex:       1,
        minWidth:   120,
      },
      {
        field:      "versions",
        headerName: t("Firmware:versions"),
        flex:       1,
        minWidth:   180,
        valueGetter: (params: any) => {
          return params.data?.versions?.join(", ") || "";
        },
      },
      {
        field:      "updateId",
        headerName: t("Firmware:update_id"),
        flex:       0,
        minWidth:   80,
        maxWidth:   100,
        valueGetter: (params: any) => {
          return params.data?.update?.id || "";
        },
      },
      {
        field:      "updateVersion",
        headerName: t("Firmware:update_version"),
        flex:       1,
        minWidth:   150,
        valueGetter: (params: any) => {
          return params.data?.update?.version || "";
        },
      },
      {
        field:      "updateFilename",
        headerName: t("Firmware:update_filename"),
        flex:       1,
        minWidth:   150,
        valueGetter: (params: any) => {
          return params.data?.update?.filename || "";
        },
      },
      {
        field:      "updateUrl",
        headerName: t("Firmware:update_url"),
        flex:       2,
        minWidth:   200,
        valueGetter: (params: any) => {
          return params.data?.update?.url || "";
        },
      },
      {
        field:      "updateFilesize",
        headerName: t("Firmware:update_filesize"),
        flex:       0,
        minWidth:   120,
        maxWidth:   150,
        valueGetter: (params: any) => {
          const size = params.data?.update?.filesize || 0;
          if (size === 0) return "";
          if (size >= 1073741824) return (size / 1073741824).toFixed(2) + " GB";
          if (size >= 1048576) return (size / 1048576).toFixed(2) + " MB";
          if (size >= 1024) return (size / 1024).toFixed(2) + " KB";
          return size + " B";
        },
      },
      {
        field:      "options",
        headerName: t("Firmware:options"),
        flex:       0,
        filter:     false,
        minWidth:   90,
        maxWidth:   90,
        cellRenderer: OptionsCell,
      },
    ]);

    const rws: FirmwareInfo[] = [];
    for (let i = 0; i < firmwares?.length; i++) {
      let firmware = copyFirmwareDescriptor(firmwares[i]);
      let firmwareAny = firmware as any;

      firmwareAny.requestDelete = () => setDeleteNum(i);
      firmwareAny.requestEdit = () => setFirmwareNum(i);
      firmwareAny.__index__ = i;

      rws.push(firmware);
    }

    setRows(rws);
  }, [firmwares, t]);

  const updateIsOpen = (val: boolean) => {
    if (!val)
      setFirmwareNum(-1);
  };

  const processDelete = React.useCallback(async (_obj: Object, answer: string) => {
    setDeleteNum(-1);

    if (answer !== "yes") {
      return;
    }

    if (deleteNum < 0 || deleteNum >= firmwares.length)
      return;

    const firmware = copyFirmwareDescriptor(firmwares[deleteNum]);
    firmware.to_delete = true;

    const mess = await store.setFirmware(firmware);
    console.log("Firmwares/processDelete/deleteNum=" + deleteNum + "/mess=", mess);
  }, [firmwares, deleteNum]);

  const updateFirmware = React.useCallback(async (n: number, firmware: FirmwareInfo) => {
    if (!firmware || n == -1)
      return;

    const nfirmware = copyFirmwareDescriptor(firmware);
    if (n == -2) {
      nfirmware.is_new = true;
    }

    const mess = await store.setFirmware(nfirmware);
    console.log("Firmwares/updateFirmware/n=" + n + "/mess=", mess);
  }, []);

  const handleCellClick = React.useCallback((evt: any) => {
    if (evt.colDef.field == "options")
      return;
    if (!role.admin && !role?.privilages?.includes("ViewFirmware"))
      return;
    setFirmwareNum(evt.data.__index__);
  }, [role]);

  const getFirmwareDesc = React.useCallback((n: number): FirmwareInfo | null => {
    if (n == -2)
      return getNewFirmwareDescriptor();

    if (n < 0 || n >= rows.length)
      return null;

    return rows[n];
  }, [rows]);

  return (
    <React.Fragment>
      <FlexRow direction="column"
               align="start"
               className={[
                 'bg-white',
                 'px-[32px] pt-[32px] sticky top-[64px] z-10',
               ]}>
        <div className="text-[32px]/[48px]">
          {t('Firmware:caption')}
        </div>
        <div>
          <NumberText>
            {rows.length}
          </NumberText>
          {' ' + t('Firmware:firmware_count')}
        </div>
        <Divider className="my-[8px]" />
        {(role.admin || role?.privilages?.includes("ChangeFirmware")) &&
          <Button caption={t('Firmware:new_firmware')}
                  icon="add"
                  className="uppercase mt-[20px]"
                  onClick={() => setFirmwareNum(-2)} />
        }
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className={[
                 "py-[16px]",
                 "px-[32px]"
               ]}>
        <Grid rowData={rows}
              columnDefs={cols}
              filter={false}
              globalFilter={true}
              showPages={true}
              maxHeight="calc(100vh - 380px)"
              onCellClicked={handleCellClick}
              pageSize={10} />
      </FlexRow>
      {firmwareNum != -1 &&
        <RightBar onOpenChange={updateIsOpen}>
          <Firmware firmware={getFirmwareDesc(firmwareNum)}
                    index={firmwareNum}
                    setFirmware={(firmware: FirmwareInfo) => updateFirmware(firmwareNum, firmware)}
                    onClose={() => setFirmwareNum(-1)} />
        </RightBar>
      }
      {deleteNum > -1 && deleteNum < rows.length &&
        <DeleteModal title={t('Firmware:delete_firmware:caption')}
                     message={t('Firmware:delete_firmware:message_n', { n: rows[deleteNum].model })}
                     onResult={processDelete}>
          <></>
        </DeleteModal>
      }
    </React.Fragment>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const role = React.useContext(RoleContext);

  const handleEdit = React.useCallback(() => {
    setOpen(false);
    props.data.requestEdit();
  }, [setOpen, props.data]);

  const handleDelete = React.useCallback(async () => {
    setOpen(false);
    props.data.requestDelete();
  }, [setOpen, props.data]);

  return (
    <Popover key={1}
             isOpen={open}
             placement="bottom-end"
             classNames={{
               content: "rounded-none",
             }}
             onOpenChange={setOpen}>
      <PopoverTrigger>
        <div className="h-full w-full text-right">
          <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
            more_vert
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <FlexRow flex={0}
                 direction="column"
                 align="start"
                 className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
          {(role.admin || role?.privilages?.includes("ChangeFirmware")) &&
            <MenuItem icon="edit_note"
                      label={t('Firmware:edit_firmware')}
                      onClick={handleEdit} />
          }
          {(role.admin || role?.privilages?.includes("DeleteFirmware")) &&
            <MenuItem icon="delete"
                      color="!text-[--Warning]"
                      label={t('Firmware:delete_firmware:caption')}
                      onClick={handleDelete} />
          }
        </FlexRow>
      </PopoverContent>
    </Popover>
  );
};

export default Firmwares;
